import { Component, OnInit, OnDestroy, SecurityContext, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AppContextService } from '../services/app-context.service';
import { Router, NavigationEnd } from '@angular/router';
import { User } from '../models/User/user';
import { ServerApiService } from '../server-api/server-api.service';
import { LoginApiService } from '../server-api/login-api.service';
import { finalize, Subscription } from 'rxjs';
import { OperationResult } from '../models/server-result/operation-result';
import { SessionExpirationTimeoutService } from '../services/authorization/session-expiration-timeout.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ToasterService } from '../shared-components/custom-toaster/toaster.service';
import { TranslateService } from '../services/translate/translate.service';
import { LoggerUserDataChangedNotificationService } from '../services/logged-user-data-changed-notification/logged-user-data-changed-notification.service';
import { EifLoggedUser, EifLoggedUserProfile } from '../models/User/eif-logged-user.type';
import { SessionStatusBroadcastService } from '../services/authorization/session-status-broadcast.service';
import { BroadcastMessage } from '../models/broadcast/broadcast-message';
import { BroadcastMessageType } from '../models/broadcast/broadcast-message-type';
import { SessionStatusBroadcastMessagePayload } from '../models/broadcast/session-status-broadcast-message-payload';
import { dropDownFX } from '../animations/animate';
import { UserRole } from '../models/enums/user-role';
import { DomSanitizer } from '@angular/platform-browser';
import { LatestDocumentViewModel } from '../models/documentation/latest-legal-document-view-model';
import { UtilitiesService } from '../services/utils/utilities.service';
import { LoginService } from '../services/authentication/login.service';
import { UserApiService } from '../server-api/user-api/user-api.service';
import { GetTokenResult } from '../models/User/auth/get-token-result';
import { MenuItem } from '../shared-components/custom-menu/custom-menu.component';
import { UserNameLabelComponent } from './design-system/user-name-label/user-name-label.component';
import { DocumentationApiService } from '../server-api/documentation-api/documentation-api.service';
import {
	LatestLegalDocumentsResult,
	LegalDocumentLinkModel,
	LegalDocumentModel,
} from '../server-api/documentation-api/documentation-api.models';
import { NavigationDocumentsComponent } from './design-system/navigation-documents/navigation-documents';
import { CustomModalRef } from '../shared-components/custom-modal/custom-modal.types';
import { CustomModalService } from '../shared-components/custom-modal/custom-modal.service';

export interface NavigationMenuGroups {
	[key: string]: {
		title?: string;
		items: NavigationMenuGroup[];
		condition?: boolean;
	};
}

export interface NavigationMenuGroup {
	text: string;
	icon?: string;
	path?: string;
	action?: (event?: Event) => void;
	condition?: boolean;
}
@Component({
	selector: 'app-private',
	templateUrl: './private.component.html',
	styleUrls: ['./private.component.scss'],
	animations: [dropDownFX],
	standalone: false,
})
export class PrivateComponent implements OnInit, OnDestroy {
	@ViewChild('verticalActions') verticalActions: ElementRef;
	@ViewChild('horizontalActions') horizontalActions: ElementRef;

	public isActiveAdminMenu: boolean = false;
	public administrationPageTooltip: string = '';
	public loggedUser: User | null = new User();
	private loggedUserData: EifLoggedUser | null = null;
	public userAccessProfiles: EifLoggedUserProfile[];

	private anyUserCompanyHasFileIntegration: boolean;
	private loggerUserDataChangedNotificationSubscription: Subscription;
	private sessionStatusBroadcastSubscription: Subscription;
	private routerSubscription: Subscription;
	private breakpointSubscription: Subscription;

	public isCollapsed: boolean = false;
	public isMobileMenuOpen: boolean = false;
	public isUserMenuOpen: boolean = false;
	public isMobile: boolean = false;

	private idleTimerSubscription: Subscription;
	private idleTimerWarningSubscription: Subscription;
	private modalTimeoutExpiredWarningRef: CustomModalRef<boolean> | null;
	private modalTimeoutExpiredInformationRef: CustomModalRef<void> | null;
	public logUsrAssociatedRle: string | null;
	public selectedMenu: string = '';
	protected userEmail: string = '';
	protected routerEventsSubscription: Subscription;
	protected latestDocuments: LatestDocumentViewModel[] = [];

	public navigationMenuItems: NavigationMenuGroups = {};
	public userMenuItems: MenuItem[] = [];

	get canSeeMonitoringMenu(): boolean {
		return (
			this.loggedUser?.IsFinancier ||
			this.loggedUser?.IsSystemAdmin ||
			this.loggedUser?.IsSupportOperator ||
			this.loggedUser?.IsBackOfficeOperator ||
			false
		);
	}

	get canSeeContractsMenu(): boolean {
		return !!(
			this.loggedUser?.IsConfirmingBuyer ||
			this.loggedUser?.IsConfirmingFinancier ||
			this.loggedUser?.IsFactoringFinancier ||
			this.loggedUser?.IsFactoringSupplier ||
			this.loggedUser?.IsSystemAdmin ||
			this.loggedUser?.IsSupportOperator ||
			this.loggedUser?.IsBackOfficeOperator
		);
	}

	get canSeeFileIntegrationMenu(): boolean {
		return this.anyUserCompanyHasFileIntegration;
	}

	get canSeeConfirmingMenu(): boolean {
		return !!(
			this.loggedUser?.IsConfirmingFinancier ||
			this.loggedUser?.IsConfirmingBuyer ||
			this.loggedUser?.IsConfirmingSupplier ||
			this.loggedUser?.IsSystemAdmin ||
			this.loggedUser?.IsSupportOperator ||
			this.loggedUser?.IsBackOfficeOperator
		);
	}

	get canSeeFactoringMenu(): boolean {
		return !!(
			this.loggedUser?.IsFactoringFinancier ||
			this.loggedUser?.IsFactoringSupplier ||
			this.loggedUser?.IsSystemAdmin ||
			this.loggedUser?.IsSupportOperator ||
			this.loggedUser?.IsBackOfficeOperator
		);
	}

	get canSeeNetWorkFinancierMenu(): boolean {
		return !!(
			this.loggedUser?.IsSystemAdmin ||
			this.loggedUser?.IsSupportOperator ||
			this.loggedUser?.IsBackOfficeOperator ||
			this.loggedUser?.IsConfirmingBuyer ||
			this.loggedUser?.IsFactoringSupplier
		);
	}

	get canSeeAdminPanel(): boolean {
		return !!(this.loggedUser?.IsSystemAdmin || this.loggedUser?.IsSupportOperator || this.loggedUser?.IsBackOfficeOperator);
	}

	constructor(
		private appContext: AppContextService,
		private router: Router,
		private serverApiService: ServerApiService,
		private loginApiService: LoginApiService,
		private userApiService: UserApiService,
		private sessionExpirationTimeoutService: SessionExpirationTimeoutService,
		private modalService: CustomModalService,
		private translateService: TranslateService,
		private toasterService: ToasterService,
		private loggerUserDataChangedNotificationService: LoggerUserDataChangedNotificationService,
		private sessionStatusBroadcastService: SessionStatusBroadcastService,
		private domSanitizer: DomSanitizer,
		private utilitiesService: UtilitiesService,
		private loginService: LoginService,
		private cdr: ChangeDetectorRef,
		private breakpointObserver: BreakpointObserver,
		private documentationApiService: DocumentationApiService
	) {
		this.isActiveAdminMenu = false;
		if (this.router.routerState.snapshot.url.endsWith('administration')) {
			this.isActiveAdminMenu = true;
		}
		this.administrationPageTooltip = this.translateService.translate('private.administration-page-tooltip', 'client');
		this.loggerUserDataChangedNotificationSubscription = this.loggerUserDataChangedNotificationService.loggedUserDataChanged.subscribe(
			(dataChanged: boolean) => {
				if (dataChanged) {
					this.setLoggedUserInfo();
				}
			}
		);
	}

	ngOnInit() {
		// Detect mobile viewport
		this.breakpointSubscription = this.breakpointObserver.observe(['(max-width: 767px)']).subscribe((result) => {
			this.isMobile = result.matches;
			this.cdr.detectChanges();
		});

		this.setLoggedUserInfo();
		this.checkIfAnyUserCompanyHasFileIntegrations();
		this.sessionTimeoutIdleMonitor();
		this.sessionTimmerBroadcast();
		this.orderAdvanceRequestsRefresh();
		this.getLatestLegalDocuments();
		this.initializeMenuSelection();
		this.subscribeToRouterEvents();

		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			this.userEmail = this.loggedUser?.Email ?? '';

			this.initializeNavigationMenuItems();
			this.updateUserMenuItems();
			this.cdr.detectChanges();
		});

		if (this.isCollapsed) {
			this.verticalActions.nativeElement.classList.add('d-block');
			this.horizontalActions.nativeElement.classList.add('d-none');
		} else {
			this.verticalActions.nativeElement.classList.add('d-none');
			this.horizontalActions.nativeElement.classList.add('d-block');
		}
		this.cdr.detectChanges();
	}

	protected onUserProfileClicked(selectedProfile: EifLoggedUserProfile): void {
		if (
			!!this.loggedUserData &&
			!!selectedProfile &&
			selectedProfile.Profile !== UserRole.SystemAdmin &&
			this.userAccessProfiles?.length > 1 &&
			!this.isProfileOfLoggedUser(selectedProfile)
		) {
			this.loginService.switchLoggedUserProfile(this.loggedUserData, selectedProfile).then((newProfile: EifLoggedUserProfile) => {
				this.sessionStatusBroadcastService.publish(<BroadcastMessage>{
					type: BroadcastMessageType.SessionProfileSwitched,
					payload: JSON.stringify(newProfile),
				});
			});
		}
	}

	protected isProfileOfLoggedUser(userData: EifLoggedUserProfile): boolean {
		if (!!userData) {
			if (userData.Profile === UserRole.SystemAdmin && this.isSystemAdmin()) {
				return true;
			}

			if (userData.Profile === UserRole.Buyer && this.isBuyer()) {
				return true;
			}

			if (userData.Profile === UserRole.Supplier && this.isSupplier()) {
				return true;
			}

			if (userData.Profile === UserRole.Financier && this.isFinancier()) {
				return true;
			}
		}

		return false;
	}

	public isProfileActive(profile: EifLoggedUserProfile): boolean {
		return this.isProfileOfLoggedUser(profile);
	}

	private handleSessionStatusBroadcastMessages(msg: BroadcastMessage): void {
		console.log(
			this.domSanitizer.sanitize(SecurityContext.HTML, this.loggedUser?.Name + '-' + this.logUsrAssociatedRle) +
				' subscribed to sessionTimmerBroadcastService'
		);

		if (msg.type == BroadcastMessageType.SessionTimmerExtended) {
			const payload = JSON.parse(msg.payload) as SessionStatusBroadcastMessagePayload;

			if (this.loggedUser!.Username === payload.Username) {
				this.sessionExpirationTimeoutService.resetTimer();
				//close modals:
				if (this.modalTimeoutExpiredWarningRef != null) this.modalTimeoutExpiredWarningRef.dismiss('tokenRefreshed');
				if (this.modalTimeoutExpiredInformationRef != null) this.modalTimeoutExpiredInformationRef.dismiss('tokenRefreshed');
			}
		} else if (msg.type == BroadcastMessageType.SessionTimmerNotExtended) {
			const payload = JSON.parse(msg.payload) as SessionStatusBroadcastMessagePayload;

			if (this.loggedUser!.Username === payload.Username) {
				//close modals:
				if (this.modalTimeoutExpiredWarningRef != null) this.modalTimeoutExpiredWarningRef.dismiss('sessionNotExtended');
				if (this.modalTimeoutExpiredInformationRef != null) this.modalTimeoutExpiredInformationRef.dismiss('sessionNotExtended');

				this.logout();
			}
		} else if (msg.type == BroadcastMessageType.SessionTimmerExpired) {
			const payload = JSON.parse(msg.payload) as SessionStatusBroadcastMessagePayload;

			if (this.loggedUser!.Username === payload.Username) {
				//close modals:
				if (this.modalTimeoutExpiredWarningRef != null) this.modalTimeoutExpiredWarningRef.dismiss('sessionNotExtended');
				if (this.modalTimeoutExpiredInformationRef != null) this.modalTimeoutExpiredInformationRef.dismiss('sessionNotExtended');

				this.logout();
			}
		} else if (msg.type == BroadcastMessageType.SessionTimmerNotExtendedDueTokenRefreshError) {
			const payload = JSON.parse(msg.payload) as SessionStatusBroadcastMessagePayload;

			if (this.loggedUser!.Username === payload.Username) {
				//close modals:
				if (this.modalTimeoutExpiredWarningRef != null) this.modalTimeoutExpiredWarningRef.dismiss('sessionNotExtended');
				if (this.modalTimeoutExpiredInformationRef != null) this.modalTimeoutExpiredInformationRef.dismiss('sessionNotExtended');

				this.logout();
			}
		} else if (msg.type == BroadcastMessageType.SessionKilled) {
			//close modals:
			if (this.modalTimeoutExpiredWarningRef != null) this.modalTimeoutExpiredWarningRef.dismiss('sessionNotExtended');
			if (this.modalTimeoutExpiredInformationRef != null) this.modalTimeoutExpiredInformationRef.dismiss('sessionNotExtended');

			this.logout();
		} else if (msg.type == BroadcastMessageType.SessionProfileSwitched) {
			const payload = JSON.parse(msg.payload) as EifLoggedUserProfile;

			if (
				!!payload &&
				!!this.loggedUserData &&
				!!this.loggedUser &&
				this.loggedUser.Username === this.loggedUserData.Username &&
				this.loggedUserData.Profiles.some((p: EifLoggedUserProfile) => p.Profile === payload.Profile)
			) {
				// PR: Force reload the new profile on this tab given that the change occurred in another tab.
				this.loginService.syncSwitchedLoggedUserProfile(payload);
			}
		}
	}

	private sessionTimmerBroadcast() {
		this.sessionStatusBroadcastSubscription = this.sessionStatusBroadcastService
			.messagesOfTypes([
				BroadcastMessageType.SessionTimmerExtended,
				BroadcastMessageType.SessionTimmerExpired,
				BroadcastMessageType.SessionTimmerNotExtended,
				BroadcastMessageType.SessionTimmerNotExtendedDueTokenRefreshError,
				BroadcastMessageType.SessionKilled,
				BroadcastMessageType.SessionProfileSwitched,
			])
			.subscribe((msg: BroadcastMessage) => {
				this.handleSessionStatusBroadcastMessages(msg);
			});
	}

	private orderAdvanceRequestsRefresh(): void {
		if (this.loggedUser?.IsFactoringSupplier) {
			//fire and forget logic. Front end does not need to wait for response.
			try {
				this.serverApiService.orderUserPendingAdvanceRequestsRefresh().subscribe();
			} catch (error) {
				console.error(error);
			}
		}
	}

	ngOnDestroy() {
		this.unsubscribeSessionTimers();

		if (this.sessionStatusBroadcastSubscription) {
			console.log('sessionStatusBroadcastSubscription.unsubscribe();');
			this.sessionStatusBroadcastSubscription.unsubscribe();
		}

		if (this.loggerUserDataChangedNotificationSubscription) {
			console.log('loggerUserDataChangedNotificationSubscription.unsubscribe();');
			this.loggerUserDataChangedNotificationSubscription.unsubscribe();
		}

		if (this.routerSubscription) {
			this.routerSubscription.unsubscribe();
		}

		if (this.breakpointSubscription) {
			this.breakpointSubscription.unsubscribe();
		}
	}

	private unsubscribeSessionTimers() {
		if (this.idleTimerSubscription) {
			this.idleTimerSubscription.unsubscribe();
			console.log('idleTimerSubscription.unsubscribe();');
		}

		if (this.idleTimerWarningSubscription) {
			this.idleTimerWarningSubscription.unsubscribe();
			console.log('idleTimerWarningSubscription.unsubscribe();');
		}
	}

	private sessionTimeoutIdleMonitor(): void {
		console.group('sessionTimeoutIdleMonitor');
		console.log('user role:' + this.domSanitizer.sanitize(SecurityContext.HTML, this.logUsrAssociatedRle));

		if (!!this.loggedUser && !!this.loggedUser.Username) {
			this.sessionExpirationTimeoutService.resetTimer();
			this.idleTimerWarningSubscription = this.sessionExpirationTimeoutService.timeoutExpiredWarning.subscribe(() => {
				this.modalTimeoutExpiredWarningRef = this.modalService.confirm({
					size: 'lg',
					title: this.translateService.translate('private.modalTimeoutExpiredWarningTitle', 'client'),
					body: {
						content: this.translateService
							.translate('private.modalTimeoutExpiredWarningContent', 'client')
							.replace('[EXPIREDTIMEMINUTES]', this.appContext.getTokenToBeExpiredWarningInMinutesOrDefault().toString()),
					},
				});

				this.modalTimeoutExpiredWarningRef.result
					.then(
						(confirmed) => {
							if (!confirmed) {
								return;
							}

							console.log('Extending session...');

							this.loginApiService.refreshJwtToken().subscribe({
								next: (operationResult: OperationResult<GetTokenResult>) => {
									this.processRefreshTokenResponse(operationResult);
								},
								error: (httpErrorResponse: HttpErrorResponse) => {
									this.processRefreshTokenCallError(httpErrorResponse);
								},
							});
						},
						(cancelData) => {
							if (cancelData !== 'dismissByTimeout' && cancelData !== 'tokenRefreshed') {
								console.log('Not extending session...');

								const messagePayload = new SessionStatusBroadcastMessagePayload(this.loggedUser!.Username!);

								let message: BroadcastMessage = <BroadcastMessage>{
									type: BroadcastMessageType.SessionTimmerNotExtended,
									payload: messagePayload.AsJson(),
								};

								this.sessionStatusBroadcastService.publish(message);
								console.log(
									this.domSanitizer.sanitize(SecurityContext.HTML, this.loggedUser?.Name + '-' + this.logUsrAssociatedRle) +
										' published msg.type:' +
										message.type
								);

								this.unsubscribeSessionTimers();
								this.logout();
							}
						}
					)
					.catch((error) => console.error('Error from modal' + this.domSanitizer.sanitize(SecurityContext.HTML, error)));
			});

			this.idleTimerSubscription = this.sessionExpirationTimeoutService.timeoutExpired.subscribe(() => {
				if (this.modalTimeoutExpiredWarningRef != null) this.modalTimeoutExpiredWarningRef.dismiss('dismissByTimeout');
				// this.sessionExpirationTimeoutService.resetTimer();
				this.unsubscribeSessionTimers();

				this.modalTimeoutExpiredInformationRef = this.modalService.alert({
					size: 'lg',
					tone: 'warning',
					title: this.translateService.translate('http-auhorization-error.modalInformationTitle', 'client'),
					body: {
						content: this.translateService.translate('http-auhorization-error.modalSucessInformationContent', 'client'),
					},
				});

				this.modalTimeoutExpiredInformationRef.result
					.then(
						() => {
							this.logout();

							const messagePayload = new SessionStatusBroadcastMessagePayload(this.loggedUser!.Username!);

							let message: BroadcastMessage = <BroadcastMessage>{
								type: BroadcastMessageType.SessionTimmerNotExtended,
								payload: messagePayload.AsJson(),
							};

							this.sessionStatusBroadcastService.publish(message);
						},
						() => {
							this.logout();
							const messagePayload = new SessionStatusBroadcastMessagePayload(this.loggedUser!.Username!);

							let message: BroadcastMessage = <BroadcastMessage>{
								type: BroadcastMessageType.SessionTimmerNotExtended,
								payload: messagePayload.AsJson(),
							};

							this.sessionStatusBroadcastService.publish(message);
						}
					)
					.catch((error) => console.error('Error from modal' + this.domSanitizer.sanitize(SecurityContext.HTML, error)));
			});
		}

		console.groupEnd();
	}

	private processRefreshTokenResponse(result: OperationResult<GetTokenResult>) {
		if (result.IsValid) {
			this.appContext.setRefreshedToken(this.loggedUser!.Username!, result.Data);

			this.sessionExpirationTimeoutService.resetTimer();

			const messagePayload = new SessionStatusBroadcastMessagePayload(this.loggedUser!.Username!);

			let message: BroadcastMessage = <BroadcastMessage>{
				type: BroadcastMessageType.SessionTimmerExtended,
				payload: messagePayload.AsJson(),
			};

			this.sessionStatusBroadcastService.publish(message);
		} else {
			const msgCouldNotLogin = this.translateService.translate('session.refresh.not.possible');
			this.toasterService.showError(msgCouldNotLogin);
		}
	}

	private processRefreshTokenCallError(httpError: HttpErrorResponse) {
		this.toasterService.showError(httpError.message);

		const messagePayload = new SessionStatusBroadcastMessagePayload(this.loggedUser!.Username!);

		let message: BroadcastMessage = <BroadcastMessage>{
			type: BroadcastMessageType.SessionTimmerNotExtended,
			payload: messagePayload.AsJson(),
		};

		this.sessionStatusBroadcastService.publish(message);

		this.logout();
	}

	private setLoggedUserInfo() {
		this.loggedUser = this.appContext.getLoggedUser();
		this.logUsrAssociatedRle = this.appContext.getLoggedUserAssociatedRole();

		if (!!this.logUsrAssociatedRle) {
			this.logUsrAssociatedRle = this.translateService.translate(this.logUsrAssociatedRle, 'list-role');
		}

		this.loggedUserData = this.appContext.getLoggedUserData(this.loggedUser?.Username!);
		this.userAccessProfiles = this.loggedUserData?.Profiles ?? [];

		this.updateUserMenuItems();
	}

	public isFinancier(): boolean {
		return this.appContext.getLoggedUserAssociatedRole() === UserRole.Financier;
	}

	public isBuyer(): boolean {
		return this.appContext.getLoggedUserAssociatedRole() === UserRole.Buyer;
	}

	public isSupplier(): boolean {
		return this.appContext.getLoggedUserAssociatedRole() === UserRole.Supplier;
	}

	public isSystemAdmin(): boolean {
		return this.appContext.getLoggedUserAssociatedRole() === UserRole.SystemAdmin;
	}

	public logout() {
		console.log('logout()');
		this.loginApiService
			.invalidateToken()
			.pipe(
				finalize(() => {
					// reset app context and go to login page
					this.appContext.removeSession();
					this.router.navigate(['public/login']);
				})
			)
			.subscribe();
	}

	public getCurrentYear(): number {
		var currentdate = new Date();
		return currentdate.getFullYear();
	}

	private checkIfAnyUserCompanyHasFileIntegrations(): void {
		//start by assuming no permission
		this.anyUserCompanyHasFileIntegration = false;

		this.userApiService.canUserAccessFileIntegrationFeatures().subscribe((opResults: OperationResult<boolean>) => {
			this.anyUserCompanyHasFileIntegration = opResults.IsValid && !!opResults.Data;

			// Re-initialize menu items to update conditions after async value is set
			if (this.loggedUser) {
				this.initializeNavigationMenuItems();
			}
		});
	}

	public goToHelpCenter() {
		//redirect to faqs page
		this.router.navigate(['private/help-center']);
	}

	public openAdminPage() {
		this.isActiveAdminMenu = true;
		this.router.navigate(['private/administration/']);
	}

	// ************ Legal documents ************

	private getLatestLegalDocuments(): void {
		this.documentationApiService.latestLegalDocuments(this.loggedUser?.LanguageCode!).subscribe({
			next: (operationResult: OperationResult<LatestLegalDocumentsResult>) => {
				if (!!operationResult?.IsValid && !!operationResult.Data) {
					this.processValidLatestLegalDocumentsResult(operationResult);
				} else {
					this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
						errors: operationResult.Errors,
						autoClose: false,
					});
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
		});
	}

	private processValidLatestLegalDocumentsResult(operationResult: OperationResult<LatestLegalDocumentsResult>): void {
		let legalDocuments: LegalDocumentModel[] | null = this.utilitiesService.ObjectDeepCopy(operationResult.Data?.Documents);
		let legalDocumentLinks: LegalDocumentLinkModel[] | null = this.utilitiesService.ObjectDeepCopy(operationResult.Data?.DocumentLinks);

		legalDocuments?.forEach((missingDoc) => {
			this.latestDocuments.push(<LatestDocumentViewModel>{
				LegalDocumentType: missingDoc.LegalDocumentType,
				DocumentBlob: this.utilitiesService.base64ToBlob(missingDoc.Document, missingDoc.ContentType),
				ContentType: missingDoc.ContentType,
				DocumentDownloadName: this.translateService.translate(missingDoc.LegalDocumentType, 'list-DownloadDocNames'),
				LegalDocumentTypeTranslation: this.translateService.translate(
					missingDoc.LegalDocumentType + '_ViewUserDropdown',
					'list-DocumentNamesLabel'
				),
			});
		});
		legalDocumentLinks?.forEach((missingDocLink: LegalDocumentLinkModel) => {
			this.latestDocuments.push(<LatestDocumentViewModel>{
				LegalDocumentType: missingDocLink.LegalDocumentType,
				DocumentLink: missingDocLink.DocumentLink,
				DocumentDownloadName: this.translateService.translate(missingDocLink.LegalDocumentType, 'list-DownloadDocNames'),
				LegalDocumentTypeTranslation: this.translateService.translate(
					missingDocLink.LegalDocumentType + '_ViewUserDropdown',
					'list-DocumentNamesLabel'
				),
			});
		});

		if (this.latestDocuments.length > 0) {
			this.latestDocuments.sort(function (doc1: LatestDocumentViewModel, doc2: LatestDocumentViewModel) {
				var doc1Code = doc1.LegalDocumentType.toUpperCase();
				var doc2Code = doc2.LegalDocumentType.toUpperCase();
				return doc1Code < doc2Code ? -1 : doc1Code > doc2Code ? 1 : 0;
			});
		}
	}

	public openLegalDocument(legalDocument: LatestDocumentViewModel) {
		if (!legalDocument) {
			return;
		}

		if (!!legalDocument.DocumentLink && !legalDocument.DocumentBlob && !legalDocument.ContentType) {
			window.open(legalDocument.DocumentLink, '_blank');
		} else {
			this.utilitiesService.openBlobNewWindowWithName(legalDocument.DocumentBlob as Blob, legalDocument.DocumentDownloadName, false);
		}
	}

	public toggleNavbar() {
		if (this.isCollapsed) {
			this.horizontalActions.nativeElement.classList.add('d-block');
			this.horizontalActions.nativeElement.classList.remove('d-none');
			setTimeout(() => {
				this.verticalActions.nativeElement.classList.add('d-none');
				this.verticalActions.nativeElement.classList.remove('d-block');
			}, 350);
		} else {
			this.verticalActions.nativeElement.classList.add('d-block');
			this.verticalActions.nativeElement.classList.remove('d-none');
			setTimeout(() => {
				this.horizontalActions.nativeElement.classList.remove('d-block');
				this.horizontalActions.nativeElement.classList.add('d-none');
			}, 350);
		}
		setTimeout(() => {
			this.isCollapsed = !this.isCollapsed;
		}, 10);
		this.cdr.detectChanges();
	}

	public getToggleIconHTML(): string {
		return this.isCollapsed ? 'fa-regular fa-arrow-right' : 'fa-regular fa-arrow-left';
	}

	public onMenuClick(path: string): void {
		this.selectedMenu = path;
		this.router.navigate([path]);
		// Close mobile menu after navigation
		this.isMobileMenuOpen = false;
	}

	public isMenuSelected(path: string): boolean {
		if (!path || !this.selectedMenu) {
			return false;
		}

		const currentPath = `private/${path}`;

		if (!this.selectedMenu.includes(currentPath)) {
			return false;
		}

		const allMenuPaths: string[] = [];
		Object.values(this.navigationMenuItems).forEach((group) => {
			if (group.condition !== false && group.items) {
				group.items.forEach((item) => {
					if (item.path && item.condition !== false) {
						allMenuPaths.push(`private/${item.path}`);
					}
				});
			}
		});

		const matchingPaths = allMenuPaths.filter((menuPath) => this.selectedMenu.includes(menuPath));

		if (matchingPaths.length === 0) {
			return false;
		}

		const mostSpecificPath = matchingPaths.reduce((longest, current) => {
			return current.length > longest.length ? current : longest;
		}, matchingPaths[0]);

		return currentPath === mostSpecificPath;
	}

	private initializeMenuSelection(): void {
		const currentUrl = this.router.url;
		this.selectedMenu = currentUrl;
	}

	private subscribeToRouterEvents(): void {
		this.routerEventsSubscription = this.router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
				this.selectedMenu = event.url;
			}
		});
	}

	private updateUserMenuItems(): void {
		this.userMenuItems = [
			{
				component: UserNameLabelComponent,
				data: {
					userName: this.loggedUser?.Name,
					userEmail: this.loggedUser?.Email,
				},
			},
			{
				divider: true,
				text: '',
			},
			{
				text: this.translateService.translate('menu.user.account'),
				icon: 'fa-solid fa-user',
				action: () => this.router.navigate([`private/users/view/${this.loggedUser?.Username}`]),
			},
			{
				text: this.translateService.translate('menu.user.changeProfile'),
				icon: 'fa-solid fa-user-friends',
				condition: this.userAccessProfiles && this.userAccessProfiles.length > 1,
				children:
					this.userAccessProfiles && this.userAccessProfiles.length > 0
						? [
								...this.userAccessProfiles.map((userProfile) => ({
									text: this.translateService.translate('menu.user.changeProfile.' + userProfile.Profile.toLowerCase()),
									action: () => this.onUserProfileClicked(userProfile),
									selected: this.isProfileActive(userProfile),
								})),
							]
						: [],
			},
			{
				text: this.translateService.translate('menu.user.needHelp'),
				icon: 'fa-solid fa-question',
				action: () => this.goToHelpCenter(),
			},
			{
				text: this.translateService.translate('menu.user.logout'),
				icon: 'fa-solid fa-sign-out',
				action: () => this.logout(),
			},
			{
				component: NavigationDocumentsComponent,
				data: {
					latestDocuments: this.latestDocuments,
					openLegalDocument: (latestDocument: LatestDocumentViewModel) => this.openLegalDocument(latestDocument),
				},
			},
		];
	}

	public noSort = () => 0;

	public initializeNavigationMenuItems(): void {
		this.navigationMenuItems = {
			main: {
				items: [
					{
						text: this.translateService.translate('menu.dashboard'),
						icon: 'fa-light fa-chart-area',
						path: 'home',
						action: () => this.onMenuClick('/private/home'),
					},
					{
						text: this.translateService.translate('menu.monitoring'),
						icon: 'fa-regular fa-chart-line',
						path: 'monitoring',
						action: () => this.onMenuClick('/private/monitoring/list'),
						condition: this.canSeeMonitoringMenu,
					},
					{
						text: this.translateService.translate('menu.documents'),
						icon: 'fa-light fa-file-invoice',
						path: 'documents',
						action: () => this.onMenuClick('/private/documents'),
					},
					{
						text: this.translateService.translate('menu.contracts'),
						icon: 'fa-light fa-file-contract',
						path: 'contracts',
						action: () => this.onMenuClick('/private/contracts'),
						condition: this.canSeeContractsMenu,
					},
					{
						text: this.translateService.translate('menu.integratedfiles'),
						icon: 'fa-light fa-file-import',
						path: 'files-integration',
						action: () => this.onMenuClick('/private/files-integration'),
						condition: this.canSeeFileIntegrationMenu,
					},
				],
			},
			confirming: {
				condition: this.canSeeConfirmingMenu,
				title: this.translateService.translate('menu.group.confirming'),
				items: [
					{
						text: this.translateService.translate('menu.funding-requests'),
						icon: 'fa-light fa-hand-holding-usd',
						path: 'funding-request',
						action: () => this.onMenuClick('/private/funding-request/list'),
					},
					{
						text: this.translateService.translate('menu.payment-request'),
						icon: 'fa-light fa-usd-circle',
						path: 'payment-request',
						action: () => this.onMenuClick('/private/payment-request/list'),
					},
				],
			},
			factoring: {
				condition: this.canSeeFactoringMenu,
				title: this.translateService.translate('menu.group.factoring'),
				items: [
					{
						text: this.translateService.translate('menu.credit-assignments'),
						icon: 'fa-light fa-hands-usd',
						path: 'credit-assignment',
						action: () => this.onMenuClick('/private/credit-assignment/list'),
					},
					{
						text: this.translateService.translate('menu.advance-requests'),
						icon: 'fa-light fa-money-check-alt',
						path: 'advance-request',
						action: () => this.onMenuClick('/private/advance-request/list'),
					},
				],
			},
			network: {
				title: this.translateService.translate('menu.group.network'),
				items: [
					{
						text: this.translateService.translate('menu.companies'),
						icon: 'fa-light fa-building',
						path: 'companies',
						action: () => this.onMenuClick('/private/companies'),
					},
					{
						text: this.translateService.translate('menu.financiers'),
						icon: 'fa-light fa-university',
						path: 'companies/financiers',
						action: () => this.onMenuClick('/private/companies/financiers'),
						condition: this.canSeeNetWorkFinancierMenu,
					},
				],
			},
		};
	}

	public getToggleTooltip(): string {
		return this.isCollapsed ? 'menu.toggleExpand' : 'menu.toggleCollapse';
	}

	public toggleMobileMenu(): void {
		if (this.isMobileMenuOpen) {
			this.isMobileMenuOpen = false;
		} else {
			this.isUserMenuOpen = false; // Close user menu if open
			this.isMobileMenuOpen = true;
		}
	}

	public closeMobileMenu(): void {
		this.isMobileMenuOpen = false;
	}

	public toggleUserMenu(): void {
		if (this.isUserMenuOpen) {
			this.isUserMenuOpen = false;
		} else {
			this.isMobileMenuOpen = false; // Close main menu if open
			this.isUserMenuOpen = true;
		}
	}

	public closeUserMenu(): void {
		this.isUserMenuOpen = false;
	}

	public handleUserMenuAction(action: (() => void) | undefined): void {
		if (action) {
			action();
			this.closeUserMenu();
		}
	}
}
